import { Client, mapUrl } from "../client";
import { Controller } from "../controller";
import { Endpoint } from "../endpoint";
import { ExtendedPagedResult, PagedResult } from "../pagedResult";
import {
	AddOrUpdateContact,
	ContactFilterField,
	ContactFilterValue,
	ContactListItem,
	ShortContactInfo,
} from "../models";
import { InfiniteQueryFnParams } from "@/hooks/infiniteQuery";
import { AxiosResponse } from "axios";
import { Note } from "@/components/organisms/history/types";
import { TransferTargetKind } from "@/common/types/transfer";

// The infinite-query hook carries a caller-defined filter bag. These are the fields the contacts queries
// put in it, named here so they are read off a type rather than off an untyped value.
interface ContactsQueryFilter {
	queueIds?: Array<string>;
	contactType?: number;
	advancedFilter?: unknown;
	requireTransferTarget?: TransferTargetKind;
}

const readContactsFilter = (filter: unknown): ContactsQueryFilter =>
	(filter ?? {}) as ContactsQueryFilter;

export const ContactsKey = "contacts";
export const MyQueuesContactsKey = "myQueuesContacts";
export const FavoritesKey = "favorites";
export const ContactsTransferKey = "contactsTransfer";
export const FavoritesTransferKey = "favoritesTransfer";
export const ContactsTransferTextKey = "contactsTransferText";
export const FavoritesTransferTextKey = "favoritesTransferText";

export const fetchContacts = ({
	page: page,
	searchValue,
	filter,
}: InfiniteQueryFnParams): Promise<ExtendedPagedResult<ContactListItem>> => {
	const contactsFilter = readContactsFilter(filter);
	const pagingFilter = {
		pageNumber: page,
		filter: searchValue,
		queueIds: contactsFilter.queueIds,
		contactType: contactsFilter.contactType,
		advancedFilter: contactsFilter.advancedFilter,
		requireTransferTarget: contactsFilter.requireTransferTarget,
	};

	return Client.instance.post(
		mapUrl(Controller.Contacts, Endpoint.List),
		pagingFilter
	);
};

export const fetchMyQueuesContacts = ({
	page: page,
	searchValue,
	filter,
}: InfiniteQueryFnParams): Promise<ExtendedPagedResult<ContactListItem>> => {
	const contactsFilter = readContactsFilter(filter);
	const pagingFilter = {
		pageNumber: page,
		filter: searchValue,
		queueIds: contactsFilter.queueIds,
		contactType: contactsFilter.contactType,
		advancedFilter: contactsFilter.advancedFilter,
		requireTransferTarget: contactsFilter.requireTransferTarget,
	};

	return Client.instance.post(
		mapUrl(Controller.Contacts, Endpoint.MyQueues, Endpoint.List),
		pagingFilter
	);
};

export const removeContact = (contactId: string): Promise<any> => {
	return Client.instance.post(
		mapUrl(Controller.Contacts, Endpoint.Delete, contactId)
	);
};

export const fetchContactsAgents = ({
	page: page,
	searchValue,
	filter,
}: InfiniteQueryFnParams): Promise<ExtendedPagedResult<ContactListItem>> => {
	const query = {
		page: page,
		searchValue,
		requireTransferTarget: readContactsFilter(filter).requireTransferTarget,
	};

	return Client.instance.get(Controller.ContactsAgents, query);
};

export const fetchFavorites = ({
	page: page,
	searchValue,
	filter,
}: InfiniteQueryFnParams): Promise<ExtendedPagedResult<ContactListItem>> => {
	const contactsFilter = readContactsFilter(filter);
	const pagingFilter = {
		pageNumber: page,
		filter: searchValue,
		queueIds: contactsFilter.queueIds,
		contactType: contactsFilter.contactType,
		advancedFilter: contactsFilter.advancedFilter,
		requireTransferTarget: contactsFilter.requireTransferTarget,
	};

	return Client.instance.post(
		mapUrl(Controller.Contacts, Endpoint.Favorites, Endpoint.List),
		pagingFilter
	);
};

export const fetchFavoritesAgents = ({
	page: page,
	searchValue,
	filter,
}: InfiniteQueryFnParams): Promise<ExtendedPagedResult<ContactListItem>> => {
	const query = {
		page: page,
		searchValue,
		requireTransferTarget: readContactsFilter(filter).requireTransferTarget,
	};

	return Client.instance.get(
		mapUrl(Controller.Contacts, Endpoint.FavoritesAgents),
		query
	);
};

// The transfer dialog offers a contact only through something it can reach them by, so it asks the server
// for contacts that carry one. Without this the list is filtered after it arrives: a page of contacts that
// carry nothing to transfer to renders empty while the total and the next-page decision still count them,
// and the dialog walks page after page of them looking for the ones it can offer. Voice can dial a number
// or address a directory name; the messaging channels only have the address.
const withTransferTarget = (
	parameters: InfiniteQueryFnParams,
	kind: TransferTargetKind
): InfiniteQueryFnParams => ({
	...parameters,
	filter: {
		...readContactsFilter(parameters.filter),
		requireTransferTarget: kind,
	},
});

export const fetchContactsForTransfer = (
	parameters: InfiniteQueryFnParams
): Promise<ExtendedPagedResult<ContactListItem>> =>
	fetchContacts(withTransferTarget(parameters, TransferTargetKind.AddressOrPhone));

export const fetchFavoritesForTransfer = (
	parameters: InfiniteQueryFnParams
): Promise<ExtendedPagedResult<ContactListItem>> =>
	fetchFavorites(withTransferTarget(parameters, TransferTargetKind.AddressOrPhone));

export const fetchContactsAgentsForTransfer = (
	parameters: InfiniteQueryFnParams
): Promise<ExtendedPagedResult<ContactListItem>> =>
	fetchContactsAgents(withTransferTarget(parameters, TransferTargetKind.Address));

export const fetchFavoritesAgentsForTransfer = (
	parameters: InfiniteQueryFnParams
): Promise<ExtendedPagedResult<ContactListItem>> =>
	fetchFavoritesAgents(withTransferTarget(parameters, TransferTargetKind.Address));

export const addToFavorites = (
	contactId: string
): Promise<PagedResult<ContactListItem>> => {
	const url = mapUrl(Controller.Contacts, Endpoint.Favorites, contactId);

	return Client.instance.post(url);
};

export const removeFromFavorites = (
	contactId: string
): Promise<PagedResult<ContactListItem>> => {
	const url = mapUrl(
		Controller.Contacts,
		Endpoint.Favorites,
		Endpoint.Delete,
		contactId
	);

	return Client.instance.post(url);
};

export const addContact = (contact: AddOrUpdateContact): Promise<any> => {
	return Client.instance.post(Controller.Contacts, contact);
};

export const updateContact = (contact: AddOrUpdateContact): Promise<any> => {
	return Client.instance.post(
		mapUrl(Controller.Contacts, Endpoint.Update),
		contact
	);
};

export const addCustomerJourneyContact = (
	addOrUpdateContact: AddOrUpdateContact
): Promise<any> => {
	return Client.instance.post(
		mapUrl(Controller.CustomerHistory),
		addOrUpdateContact
	);
};

export const updateCustomerJourneyContact = (
	addOrUpdateContact: AddOrUpdateContact
): Promise<any> => {
	return Client.instance.post(
		mapUrl(Controller.CustomerHistory, Endpoint.Update),
		addOrUpdateContact
	);
};

export const searchContactsByEmail = (
	value: string
): Promise<ShortContactInfo[]> => {
	return !!value.trim()
		? Client.instance.get(mapUrl(Controller.Contacts, Endpoint.Email), {
			value: value,
		})
		: Promise.resolve([]);
};

export const searchContactsByPhone = (
	value: string
): Promise<ShortContactInfo[]> => {
	return !!value.trim()
		? Client.instance.get(mapUrl(Controller.Contacts, Endpoint.Phone), {
			value: value,
		})
		: Promise.resolve([]);
};

export const fetchContactFilterValues = (
	field: ContactFilterField,
	value: string,
	signal?: AbortSignal
): Promise<ContactFilterValue[]> => {
	return Client.instance.get(
		mapUrl(Controller.Contacts, Endpoint.FilterValues),
		{ field: field, value: value },
		signal
	);
};

export const searchContactsByName = (
	value: string
): Promise<ContactListItem[]> => {
	return !!value.trim()
		? Client.instance.get(mapUrl(Controller.Contacts, Endpoint.Name), {
			value: value,
		})
		: Promise.resolve([]);
};

export const searchContactsById = (
	value: string
): Promise<ContactListItem | null> => {
	return !!value.trim()
		? Client.instance.get(mapUrl(Controller.Contacts, Endpoint.Id), {
			value: value,
		})
		: Promise.resolve(null as any);
};

export const getNotesByContactId = (contactId: string): Promise<Note[]> => {
	return Client.instance.get(mapUrl(Controller.Contacts, Endpoint.Notes), {
		contactId: contactId,
	});
};

export const createContactNote = (note: Note): Promise<AxiosResponse> => {
	return Client.instance.post(
		mapUrl(Controller.Contacts, Endpoint.Notes),
		note
	);
};
